import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback } from "react";
import { useAppContext } from "../../AppProvider";
import { ModalConfig } from "../../Models/ModalConfig";
import { useAddressBookIndexContext } from "./providers";
import {
  faBuilding,
  faHouse,
  faPenToSquare,
  faTrash,
} from "../../utilities/fontawesomeIcons";
import { AddressBook } from "../../Models/AddressBook";

const AddressBookListItem: React.FC<{
  listRef: React.RefObject<{
    refresh: () => void;
  } | null>;
  item: AddressBook;
  index: number;
}> = React.memo(({ item }) => {
  const { dictionary, breakpoint } = useAppContext();
  const { setModalConfig } = useAddressBookIndexContext();

  const openModal = useCallback(
    (
      action: ModalConfig["action"],
      data?: string[],
      size: ModalConfig["size"] = "sm",
      className = "",
      title = "",
    ) => {
      setModalConfig({
        isOpen: true,
        action,
        size,
        data,
        className,
        title,
      });
    },
    [setModalConfig],
  );

  const isSmall = ["xs", "sm"].includes(breakpoint);

  const gridTemplateColumns = isSmall
    ? "1fr 1fr"
    : "0.25fr 1fr 1fr 2fr 1fr 0.75fr auto";

  const gridTemplateAreas = isSmall
    ? `
      " contactName name "
      " address address "
      " email email"
      " phone phone "
      " hr hr"
      " action action"
    `
    : `
      "priv contactName name address email phone action"
    `;

  return (
    <div className="list-item px-2">
      <div
        className="d-grid grid-column align-items-center"
        style={{
          gridTemplateColumns: gridTemplateColumns,
          gridTemplateAreas: gridTemplateAreas,
          gap: "0.5rem 1rem",
        }}
      >
        <div className="text-center" style={{ gridArea: "priv" }}>
          <FontAwesomeIcon
            icon={item?.priv ? faHouse : faBuilding}
            className="cursor-pointer"
            title={
              item?.priv
                ? dictionary.words.private
                : dictionary.registries.company
            }
          />
        </div>
        <div style={{ gridArea: "contactName" }}>
          <label className="label-2">
            {dictionary.registries.name} {dictionary.words.and}{" "}
            {dictionary.users.surname}
          </label>
          <label className="label-3">{item.contactName || "-"}</label>
        </div>
        <div style={{ gridArea: "name" }}>
          <label className="label-2">
            {dictionary.registries.business_name}
          </label>
          <label className="label-3">{item.name || "-"}</label>
        </div>
        <div style={{ gridArea: "address" }}>
          <label className="label-2">{dictionary.locations.address}</label>
          <label className="label-3">{item.location.label || "-"}</label>
        </div>
        <div style={{ gridArea: "email" }}>
          <label className="label-2">{dictionary.registries.email}</label>
          <label className="label-3">{item.email || "-"}</label>
        </div>
        <div style={{ gridArea: "phone" }}>
          <label className="label-2">{dictionary.registries.phone}</label>
          <label className="label-3">{item.phone || "-"}</label>
        </div>

        {isSmall && <hr style={{ gridArea: "hr" }} className="my-1" />}

        <div className="text-end" style={{ gridArea: "action" }}>
          <button
            title={dictionary.actions.edit}
            className={"button-1 button-1-light button-1-icon me-1"}
            onClick={() => {
              openModal("edit", [item.id], "md", "", dictionary.actions.edit);
            }}
          >
            <FontAwesomeIcon icon={faPenToSquare} />
          </button>
          <button
            title={dictionary.actions.delete}
            className="button-1 button-1-danger button-1-icon"
            onClick={() => {
              openModal(
                "delete",
                [item.id],
                "sm",
                "",
                dictionary.actions.delete,
              );
            }}
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </div>
      </div>
    </div>
  );
});

export default AddressBookListItem;
